import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr, isInternal } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { DateInput, fmtDate } from "@/components/DateInput";
import { KmkTrend } from "@/components/KmkTrend";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, Calculator, Search, Loader2, Landmark, BellRing, Download } from "lucide-react";
import { exportRows } from "@/components/TableTools";

const today = () => new Date().toISOString().slice(0, 10);
const rateFmt = (v) => Number(v || 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const FIN_ROLES = ["super_admin", "supervisor", "staff_keuangan", "leader"];

function Change({ v }) {
  if (v === undefined || v === null) return <span className="text-muted-foreground">—</span>;
  const cls = v > 0 ? "text-emerald-700" : v < 0 ? "text-rose-600" : "text-muted-foreground";
  return <span className={`num ${cls}`}>{v > 0 ? "▲ +" : v < 0 ? "▼ −" : "■ "}{rateFmt(Math.abs(v))}</span>;
}

function FxBanks({ banks, week, lang }) {
  const fx = banks.filter((b) => (b.currency || "IDR") !== "IDR");
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="kmk-fx-banks">
      <span className="text-muted-foreground">{fl("kmkFxBanks", lang)}:</span>
      {fx.length === 0 && <span className="text-muted-foreground italic">{fl("kmkNoFxBank", lang)}</span>}
      {fx.map((b) => (
        <span key={b.id} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1" data-testid={`kmk-bank-${b.id}`}>
          <Landmark className="h-3 w-3" /><span className="num font-semibold">{b.code || b.name}</span>
          <span className="num text-muted-foreground">1 {b.currency} = Rp {rateFmt(week?.rates?.[b.currency])}</span>
        </span>
      ))}
    </div>
  );
}

export function KmkTab({ clientId, banks }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [d, setD] = useState(today());
  const [week, setWeek] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    if (!d) return;
    setBusy("load");
    try { setWeek((await api.get(`/kurs/kmk?date=${d}`)).data); } catch (e) { setWeek(null); toast.error(apiErr(e.response?.data?.detail)); }
    setBusy("");
  }, [d]);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setBusy("sync");
    try { setWeek((await api.post(`/kurs/kmk/sync?date=${d}`)).data); toast.success("KMK OK"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy("");
  };
  const recalc = async () => {
    if (!window.confirm(fl("kmkRecalcConfirm", lang))) return;
    setBusy("recalc");
    try { const { data } = await api.post(`/kurs/recalc?client_id=${clientId}`); toast.success(fl("kmkRecalcDone", lang).replace("{n}", data.updated)); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy("");
  };
  const notifyMe = async () => {
    setBusy("notify");
    try { await api.post("/kurs/kmk/notify-me"); toast.success(fl("kmkNotifyDone", lang)); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy("");
  };
  const rows = Object.entries(week?.rates || {}).filter(([c]) => !q || c.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card className="p-4 space-y-4" data-testid="kmk-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="font-head font-semibold">{fl("kmkTitle", lang)}</h2>
          <p className="text-xs text-muted-foreground mt-1">{fl("kmkHint", lang)}</p>
          <p className="text-xs text-muted-foreground mt-1" data-testid="kmk-weekly-hint">{fl("kmkWeeklyHint", lang)}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">{fl("kmkDate", lang)}</label>
            <DateInput value={d} onChange={(e) => setD(e.target.value)} className="w-40" data-testid="kmk-date" /></div>
          <Button variant="outline" size="sm" className="h-9" onClick={notifyMe} disabled={!!busy} data-testid="kmk-notify-me">
            {busy === "notify" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BellRing className="h-4 w-4 mr-1" />}{fl("kmkNotifyMe", lang)}</Button>
          {isInternal(user.role) && <Button variant="outline" size="sm" className="h-9" onClick={sync} disabled={!!busy} data-testid="kmk-sync">
            {busy === "sync" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}{fl("kmkSync", lang)}</Button>}
          {FIN_ROLES.includes(user.role) && <Button size="sm" className="h-9" onClick={recalc} disabled={!!busy || !clientId} data-testid="kmk-recalc">
            {busy === "recalc" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}{fl("kmkRecalc", lang)}</Button>}
        </div>
      </div>
      {week && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="kmk-week">
          <span className="font-semibold" data-testid="kmk-no">KMK {week.kmk_no}</span>
          <span className="text-muted-foreground">· {fl("kmkValid", lang)} <span className="num" data-testid="kmk-valid">{fmtDate(week.start)} – {fmtDate(week.end)}</span></span>
          {week.fallback && <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="kmk-fallback">{fl("kmkFallback", lang)}</Badge>}
        </div>
      )}
      <FxBanks banks={banks} week={week} lang={lang} />
      <KmkTrend currencies={banks.filter((b) => (b.currency || "IDR") !== "IDR").map((b) => b.currency)} lang={lang} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-56"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="USD, SGD…" className="pl-9 h-9" data-testid="kmk-search" /></div>
        <Button variant="outline" size="sm" className="h-9" disabled={!rows.length} data-testid="kmk-export"
          onClick={() => exportRows(rows.map(([c, r]) => ({ c, r, ch: week?.changes?.[c] ?? "" })), [["Mata Uang", "c"], ["Kurs KMK (Rp)", "r"], ["Perubahan", "ch"]], `kurs_kmk_${week?.start || d}`)}>
          <Download className="h-4 w-4 mr-1" />Ekspor</Button>
      </div>
      <div className="overflow-auto rounded-md border border-border max-w-xl">
        <Table>
          <TableHeader><TableRow><TableHead>{fl("kmkCurrency", lang)}</TableHead><TableHead className="text-right">{fl("kmkRate", lang)}</TableHead><TableHead className="text-right">{fl("kmkChange", lang)}</TableHead></TableRow></TableHeader>
          <TableBody>
            {busy === "load" && <TableRow><TableCell colSpan={3} className="py-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /></TableCell></TableRow>}
            {busy !== "load" && rows.length === 0 && <TableRow><TableCell colSpan={3} className="py-6 text-center text-muted-foreground">—</TableCell></TableRow>}
            {busy !== "load" && rows.map(([c, r]) => (
              <TableRow key={c} data-testid={`kmk-row-${c}`}>
                <TableCell className="num font-semibold">{c}</TableCell>
                <TableCell className="num text-right">{rateFmt(r)}</TableCell>
                <TableCell className="text-right text-xs" data-testid={`kmk-change-${c}`}><Change v={week?.changes?.[c]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-muted-foreground">{fl("kmkPer100", lang)}</p>
    </Card>
  );
}
